import { useState } from 'react';
import { C, DEGRADE_ACTION } from '../../theme';
import { Champ, styleChamp } from './champs';
import { api } from '../../api/routes';
import { messageDErreur } from '../../api/useDonnees';

/**
 * Reparer l'acces d'un compte ENFANT (lot E2).
 *
 * Constate en production le 2026-08-27 : un compte enfant cree avec une adresse mal tapee
 * etait inutilisable — et irreparable, aucune route ne changeant l'email. Le compte
 * n'avait jamais eu une seule session.
 *
 * Reserve aux comptes enfants cote serveur : un adulte ne prend jamais la main sur le
 * compte d'un autre adulte.
 */
export function IdentifiantsEnfant({
  membreId,
  prenom,
  email,
  onFait,
  signaler,
}: {
  membreId: string;
  prenom: string;
  email: string;
  onFait: (message: string) => void;
  signaler: (message: string) => void;
}) {
  const [ouvert, setOuvert] = useState<'email' | 'motDePasse' | null>(null);
  const [saisie, setSaisie] = useState('');
  const [envoi, setEnvoi] = useState(false);

  const envoyer = async () => {
    const valeur = saisie.trim();
    if (valeur === '' || envoi || ouvert === null) return;
    setEnvoi(true);
    try {
      if (ouvert === 'email') {
        await api.changerIdentifiantsEnfant(membreId, { email: valeur });
        onFait(`Adresse changée. ${prenom} peut maintenant se connecter avec cette adresse.`);
      } else {
        await api.changerIdentifiantsEnfant(membreId, { password: valeur });
        onFait(`Nouveau mot de passe enregistré. Les appareils de ${prenom} ont été déconnectés.`);
      }
      setOuvert(null);
      setSaisie('');
    } catch (cause) {
      signaler(messageDErreur(cause));
    } finally {
      setEnvoi(false);
    }
  };

  const bouton = (style?: 'principal'): React.CSSProperties => ({
    border: style === 'principal' ? 'none' : `1px solid ${C.violetBordure}`,
    background: style === 'principal' ? DEGRADE_ACTION : C.carte,
    color: style === 'principal' ? '#FFFFFF' : C.violetTexte,
    borderRadius: 10,
    padding: '7px 12px',
    fontSize: 12.5,
    fontWeight: 800,
    fontFamily: 'inherit',
    cursor: 'pointer',
  });

  return (
    <div
      style={{
        background: C.carte,
        border: `1px solid ${C.bordure}`,
        borderRadius: 14,
        padding: '12px 16px',
        marginTop: -4,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div style={{ fontSize: 12, color: C.secondaire, wordBreak: 'break-all' }}>
        Se connecte avec : <strong style={{ color: C.encre }}>{email}</strong>
      </div>

      {ouvert === null ? (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="pressable"
            aria-label={`Changer l’adresse email de ${prenom}`}
            onClick={() => {
              setSaisie(email);
              setOuvert('email');
            }}
            style={bouton()}
          >
            Changer l’adresse
          </button>
          <button
            type="button"
            className="pressable"
            aria-label={`Définir un nouveau mot de passe pour ${prenom}`}
            onClick={() => {
              setSaisie('');
              setOuvert('motDePasse');
            }}
            style={bouton()}
          >
            Nouveau mot de passe
          </button>
        </div>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void envoyer();
          }}
          style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
        >
          <Champ libelle={ouvert === 'email' ? 'Nouvelle adresse' : 'Nouveau mot de passe'}>
            <input
              type={ouvert === 'email' ? 'email' : 'text'}
              value={saisie}
              onChange={(e) => setSaisie(e.target.value)}
              aria-label={
                ouvert === 'email'
                  ? `Nouvelle adresse email de ${prenom}`
                  : `Nouveau mot de passe de ${prenom}`
              }
              autoFocus
              style={styleChamp}
            />
          </Champ>
          {ouvert === 'motDePasse' ? (
            <div style={{ fontSize: 12, color: C.tertiaire, marginTop: -6 }}>
              Ses appareils déjà connectés seront déconnectés.
            </div>
          ) : null}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="submit"
              className="pressable"
              disabled={envoi || saisie.trim() === ''}
              style={{
                ...bouton('principal'),
                flex: 1,
                height: 40,
                background: envoi || saisie.trim() === '' ? C.inactif : DEGRADE_ACTION,
              }}
            >
              {envoi ? '…' : 'Enregistrer'}
            </button>
            <button
              type="button"
              className="pressable"
              onClick={() => {
                setOuvert(null);
                setSaisie('');
              }}
              style={{
                ...bouton(),
                height: 40,
                borderColor: C.bordure,
                color: C.secondaire,
                fontWeight: 700,
              }}
            >
              Annuler
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
